/**
 * AuthProvider (stage 5 rewrite): username + password app wall.
 *
 * The email-based Supabase Auth flow is suspended for the web UI (signup,
 * verify, reset and pricing were removed by user decision). Sessions are
 * HMAC tokens issued by the license server (`POST /auth/login`) and stored
 * in localStorage; the bearer token is sent on calls that need it.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type SessionUser = {
  username: string;
  role: 'admin' | 'user';
  displayName?: string;
  mustChangePassword: boolean;
};

type AuthState = {
  user: SessionUser | null;
  loading: boolean;
  error: string | null;
  sessionToken: string | null;
  login: (username: string, password: string) => Promise<SessionUser>;
  logout: () => Promise<void>;
  clearError: () => void;
};

import { SESSION_TOKEN_KEY, getStoredSessionToken, AUTH_CHANGED_EVENT } from './sessionToken';

function serverUrl(): string {
  const url = (import.meta.env['VITE_LICENSE_SERVER_URL'] as string | undefined) ?? '';
  return url.replace(/\/$/, '');
}

/** Decodes ONLY the payload the server already signed; auth is enforced
 * server-side per API call — the client value is just for first paint. */
function userFromToken(token: string): SessionUser | null {
  try {
    const exp = JSON.parse(atob(token.split('.')[0])) as {
      username: string; role: 'admin' | 'user'; exp: number;
    };
    if (!exp?.username || exp.exp * 1000 < Date.now()) return null;
    return { username: exp.username, role: exp.role, mustChangePassword: false };
  } catch {
    return null;
  }
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    try {
      const t = getStoredSessionToken();
      return t && userFromToken(t) ? t : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      if (token) localStorage.setItem(SESSION_TOKEN_KEY, token);
      else localStorage.removeItem(SESSION_TOKEN_KEY);
    } catch {
      /* storage unavailable — session lives for this tab only */
    }
  }, [token]);

  const login = useCallback(async (username: string, password: string): Promise<SessionUser> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${serverUrl()}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const text = await res.text();
      const data = text ? (JSON.parse(text) as Record<string, unknown>) : {};
      if (!res.ok || !data.token) {
        throw new Error((data.error as string) ?? `HTTP ${res.status}`);
      }
      const user = userFromToken(data.token as string)!;
      // preserve server flags the token does not carry
      user.mustChangePassword = Boolean(data.mustChangePassword);
      user.displayName = (data.displayName as string) ?? undefined;
      if (user.role !== 'admin') user.mustChangePassword = Boolean(data.mustChangePassword);
      setToken(data.token as string);
      // Persist SYNCHRONOUSLY before the auth-changed event: LicenseProvider's
      // listener reads storage the instant the event fires (an effect would
      // run after render and lose the race).
      try {
        localStorage.setItem(SESSION_TOKEN_KEY, data.token as string);
      } catch {
        /* storage unavailable — effect will retry */
      }
      window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
      return user;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(
        /fetch|network/i.test(msg)
          ? 'Sunucuya ulaşılamadı — license sunucusu (apps/server) çalışıyor mu?'
          : msg === 'wrong username or password'
            ? 'Kullanıcı adı veya şifre hatalı'
            : msg,
      );
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setToken(null);
    // Clear storage BEFORE the event so listeners see the logged-out state.
    try {
      localStorage.removeItem(SESSION_TOKEN_KEY);
    } catch {
      /* storage unavailable — effect will clean later */
    }
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  }, []);

  const user = useMemo(() => (token ? userFromToken(token) : null), [token]);

  const value = useMemo<AuthState>(
    () => ({ user, loading, error, sessionToken: token, login, logout, clearError: () => setError(null) }),
    [user, loading, error, token, login, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}

/** Session bearer for API calls that need it (admin panel etc.). */
export function useAuthFetch() {
  const { sessionToken, logout } = useAuth();
  return useCallback(
    async (path: string, method: 'GET' | 'POST' = 'GET', body?: unknown): Promise<Record<string, unknown>> => {
      const res = await fetch(`${serverUrl()}${path}`, {
        method,
        headers: {
          ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
          ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      const text = await res.text();
      const data = text ? (JSON.parse(text) as Record<string, unknown>) : {};
      if (res.status === 401) {
        await logout(); // expired/invalid session → clean state
        throw new Error((data.error as string) ?? 'oturum süresi doldu');
      }
      if (!res.ok) throw new Error((data.error as string) ?? `HTTP ${res.status}`);
      return data;
    },
    [sessionToken, logout],
  );
}
